import axios from "axios"

import {apiClient} from "@/lib/api/client"
import {otkDocumentFieldLabel} from "@/lib/documentEditor"
import type {
    CreatedDraftDocument,
    DocumentsPageResult,
    DocumentsSearchPageResult,
    DocumentStatus,
    OtkDocumentCounts,
    OtkDocumentDetail,
    OtkDocumentDraftPatch,
    OtkDocumentFilesResponse,
    OtkDocumentPhoto,
    OtkDocumentSearchResult,
    OtkDocumentsSearchInput,
    OtkDocumentView,
    SaveNonconformitiesInput,
    SaveReconstructionsInput,
    SignedOtkDocumentPayload,
    Sort,
} from "shared"
import {
    createdDraftDocumentSchema,
    documentsPageResultSchema,
    documentsSearchPageResultSchema,
    otkDocumentCountsSchema,
    otkDocumentDetailSchema,
    otkDocumentFilesResponseSchema,
    signedOtkDocumentPayloadSchema,
} from "shared"

export type {
  DocumentsPageResult,
  DocumentsSearchPageResult,
  DocumentStatus,
  OtkDocumentPhoto,
  OtkDocumentSearchResult,
  OtkDocumentsSearchInput,
  OtkDocumentView,
  Sort,
  CreatedDraftDocument,
  OtkDocumentDraftPatch,
}

// otk-documents.ts's `GET /otk/documents` — `status` is required and
// single-valued (not repeatable, unlike documents.md's own idealized
// `status` (repeatable) query param), cursor-paginated, `pageSize` capped
// at 50 server-side (`documentsListQuery`). One page (30 rows by default)
// per call — the UI fetches only the active tab, 30 at a time, with an
// explicit "Завантажити ще" button driving further pages (see
// `store/documents.ts`), rather than draining every cursor page up front.
export async function getOtkDocumentsPage({
  cursor,
  pageSize = 30,
  sort = "DESC",
  status,
}: {
  cursor?: string
  pageSize?: number
  sort?: Sort
  status: DocumentStatus
}): Promise<DocumentsPageResult> {
  const { data } = await apiClient.get<unknown>("/otk/documents", {
    params: { cursor, pageSize, sort, status },
  })
  return documentsPageResultSchema.parse(data)
}

export async function getOtkDocumentCounts(): Promise<OtkDocumentCounts> {
  const {data} = await apiClient.get<unknown>("/otk/documents/counts")
  return otkDocumentCountsSchema.parse(data)
}

// otk-documents.ts's `POST /otk/documents/search` — resolves OPEN-QUESTIONS.md
// item 102 option (a): the response (`OtkDocumentSearchResult`) carries every
// searchable vehicle/owner field (`registrationCertificate`, `vehicleType`,
// `ownerType`, `ownerName`, `ownerTaxId`), not just the main list's
// `vehicleDisplay`/`vin`. `input` is sent as-is — its shape already matches
// `otkDocumentsSearchInputSchema` exactly, so there's no separate UI-side
// params type to keep in sync with the backend contract.
export async function searchOtkDocuments(
  input: OtkDocumentsSearchInput
): Promise<DocumentsSearchPageResult> {
  const { data } = await apiClient.post<unknown>(
    "/otk/documents/search",
    input
  )
  return documentsSearchPageResultSchema.parse(data)
}

export async function createOtkDocument(): Promise<CreatedDraftDocument> {
  const { data } = await apiClient.post<unknown>("/otk/documents/create");
  return createdDraftDocumentSchema.parse(data)
}

export async function getOtkDocumentById(id: string): Promise<OtkDocumentDetail> {
  const {data} = await apiClient.get<unknown>(`/otk/documents/${id}`);
  return otkDocumentDetailSchema.parse(data);
}

// Send the byte-for-byte JSON string signed by the КЕП adapter. Axios must not
// serialize it again because even an equivalent JSON representation would no
// longer match the detached signature.
export async function submitSignedOtkDocument(
  id: string,
  signedPayload: string,
  kepSignature: string
): Promise<OtkDocumentDetail> {
  const { data } = await apiClient.post<unknown>(
    `/otk/documents/${id}/sign`,
    signedPayload,
    {
      transformRequest: [(body: string) => body],
      headers: {
        "Content-Type": "application/json",
        "X-KEP-Signature": kepSignature,
      },
    }
  )
  return otkDocumentDetailSchema.parse(data)
}

// Mirrors the backend's own gate (`signedOtkDocumentPayloadSchema.safeParse`
// in `kep-verifier.ts`, run against the signed bytes) so a malformed payload
// is caught before the user is asked to touch their KEP key/token, not only
// after a doomed round-trip to `POST /otk/documents/:id/sign`.
export class OtkDocumentValidationError extends Error {
  readonly fields: string[]

  constructor(fields: string[]) {
    super("OTK_DOCUMENT_VALIDATION_FAILED")
    this.name = "OtkDocumentValidationError"
    this.fields = fields
  }
}

export async function retryOtkDocument(id: string): Promise<OtkDocumentDetail> {
  const {data} = await apiClient.post<unknown>(`/otk/documents/${id}/retry`)
  return otkDocumentDetailSchema.parse(data)
}

export function serializeOtkDocumentForSigning(payload: SignedOtkDocumentPayload): string {
  const parsed = signedOtkDocumentPayloadSchema.safeParse(payload)
  if (!parsed.success) {
    const fields = [
      ...new Set(parsed.error.issues.map((issue) => otkDocumentFieldLabel(issue.path))),
    ]
    throw new OtkDocumentValidationError(fields)
  }
  return JSON.stringify(parsed.data)
}

export async function patchOtkDocument(id: string, patch: OtkDocumentDraftPatch): Promise<OtkDocumentDetail> {
  const { data } = await apiClient.patch<unknown>(`/otk/documents/${id}`, patch);
  return otkDocumentDetailSchema.parse(data);
}

// otk-documents.ts's `DELETE /otk/documents/:id` — permanently deletes a draft,
// gated by the same optimistic-concurrency `version` check as
// `patchOtkDocument`. Non-drafts and stale versions both return 409.
export async function deleteOtkDocument(id: string, version: number): Promise<void> {
  await apiClient.delete(`/otk/documents/${id}`, { params: { version } });
}

// otk-documents.ts's `PUT /otk/documents/:id/nonconformities` — replaces the
// draft's full nonconformities list (and re-derives `type`/`result`) in one
// call, unlike the generic per-field `PATCH`.
export async function saveOtkDocumentNonconformities(
  id: string,
  body: SaveNonconformitiesInput
): Promise<OtkDocumentDetail> {
  const { data } = await apiClient.put<unknown>(`/otk/documents/${id}/nonconformities`, body);
  return otkDocumentDetailSchema.parse(data);
}

// otk-documents.ts's `PUT /otk/documents/:id/reconstructions` — replaces the
// draft's full reconstructions list (and re-derives `type`/`result`) in one
// call, unlike the generic per-field `PATCH`.
export async function saveOtkDocumentReconstructions(
  id: string,
  body: SaveReconstructionsInput
): Promise<OtkDocumentDetail> {
  const { data } = await apiClient.put<unknown>(`/otk/documents/${id}/reconstructions`, body);
  return otkDocumentDetailSchema.parse(data);
}

export async function getOtkDocumentFilesWithUrl(id: string): Promise<OtkDocumentFilesResponse> {
  const {data} = await apiClient.get<unknown>(`/otk/documents/${id}/files/with-url`)
  return otkDocumentFilesResponseSchema.parse(data)
}

// otk-documents.ts's `DELETE /otk/documents/:id/files/:pathname`.
export async function deleteOtkDocumentFile(
  id: string,
  pathname: string
): Promise<OtkDocumentDetail> {
  const { data } = await apiClient.delete<unknown>(
    `/otk/documents/${id}/files/${encodeURIComponent(pathname)}`
  )
  return otkDocumentDetailSchema.parse(data)
}

const DELETE_FILE_RETRY_DELAYS_MS = [500, 1000, 2000, 4000]

// The browser uploads straight to Vercel Blob and only afterward does a
// webhook (`onUploadCompleted`) write the file's ref into the document — so
// a delete requested right after upload can race that webhook and 403
// (`DOCUMENT_FILE_NOT_FOUND`) even though the blob itself already exists.
// Retries only that specific case; any other failure rethrows immediately.
export async function deleteOtkDocumentFileWithRetry(
  id: string,
  pathname: string
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await deleteOtkDocumentFile(id, pathname)
      return
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      if (status !== 403 || attempt >= DELETE_FILE_RETRY_DELAYS_MS.length) throw error
      await new Promise((resolve) =>
        setTimeout(resolve, DELETE_FILE_RETRY_DELAYS_MS[attempt])
      )
    }
  }
}
